import { GetConsentStatusForRedirect } from "__generated__/types";
import { Button } from "common/buttons";
import { NormalContentLayout } from "common/components/layouts/NormalContentLayout";
import { Section } from "common/components/Section";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import {
  getLocalStorageValue,
  saveLocalStorageValue,
} from "common/local-storage";
import { urls } from "common/urls";
import { termsOfUseBreadcrumb } from "domain/pages/breadcrumbs";
import { ConsentForm, consentStatusFragment } from "domain/profile/ConsentForm";
import gql from "graphql-tag";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";

const GET_CONSENT_STATUS = gql`
  query GetConsentStatusForRedirect {
    consentStatusList {
      ...ConsentStatusFragment
    }
  }
  ${consentStatusFragment}
`;

/* NB: This version must be bumped if there are major changes to the terms of use text below. This will trigger
 * the user to be redirected to this page when there is a new version available. */
export const TERMS_VERSION = "TERMS_V4";

const seenTermFromLocalStorage = getLocalStorageValue({
  key: "termsVersion",
  defaultValue: null,
});

export const TermsOfUsePage: React.FC = () => {
  const [haveSeenCurrentTermsVersion, setHaveSeenCurrentTermsVersion] =
    useState(seenTermFromLocalStorage === TERMS_VERSION);

  const { data } =
    useSimpleQuery<GetConsentStatusForRedirect>(GET_CONSENT_STATUS);

  const shouldCompleteConsent = data?.consentStatusList.some(
    (consent) => !consent.completed,
  );

  const navigate = useNavigate();

  const handleHaveReadTerms = () => {
    if (!haveSeenCurrentTermsVersion) {
      saveLocalStorageValue({
        key: "termsVersion",
        id: null,
        value: TERMS_VERSION,
      });

      setHaveSeenCurrentTermsVersion(true);
      navigate(0);
    }
  };

  useEffect(() => {
    if (haveSeenCurrentTermsVersion && !shouldCompleteConsent) {
      navigate(urls.frontPage);
    }
  }, []);

  return (
    <NormalContentLayout
      title="Brukervilkår"
      breadcrumbItems={termsOfUseBreadcrumb}
    >
      <Section title="Innhold">
        <p>
          Denne avtalen gjelder for prosjektet Adapt/Ferdighetsbasert læring fra
          Universitetet i Oslo v/Jan Magnus Aronsen.
        </p>
        <p>
          Feide benyttes for innlogging i løsningen. E-post og navn hentes fra
          Feide og er nødvendig for å registrere deg som bruker. Disse
          opplysningene og andre data knyttet til din bruker blir lagret i en
          database som oppbevares i AWS EU/EØS. Navnet ditt kan vises til lærere
          der det er kontaktflater mellom lærer og student, dette kan
          eksempelvis være tilbakemeldinger eller spørsmål. Det vil på sikt bli
          mulig å være anonym i din kommunikasjon med lærer.
        </p>
        <p>
          Ved bruk av Adapt behandles også personrelaterte opplysninger som er
          teknisk nødvendig for å yte teknisk support i feilsituasjoner, kunne
          feilsøke ved disse feilsituasjonene, bevare sikkerhet, samt å kunne
          avdekke misbruk avløsningen. Disse opplysningene lagres i en kort
          periode for å oppnå ovennevnte formål: IP-adresse, innhold i
          forespørselen og størrelsen dens, tidspunkt for forespørselen, hvilken
          side forespørselen gjøres fra, HTTP-statuskode, nettlesertype og
          nettleserversjon.
          <i>
            Juridisk grunnlagfor behandlingen er art. 6 avsn. 1 s. 1 bokstav b
            og f i EUs personvernforordning (GDPR).
          </i>
        </p>
        <p>
          Vi bruker også en tjeneste til å detektere og rette opp i feil som
          oppstår når du bruker Adapt, samt for å forbedre brukeropplevelsen.
          Dataene som innhentes er anonymisert, for eksempel er dette data om
          tidspunkt for feil og hvilken enhetstype som ble brukt. Dataene blir
          lagret opp til 90 dager. Dersom du skulle ønske å slette brukeren din
          før pilotperioden går ut kan du gjøre dette via din profil.
        </p>
        <p>
          Som bruker av denne løsningen skal du <b>ikke</b> legge ut
          personopplysninger om deg selv eller andre der det er mulig å dele
          data med andre. For studenter gjelder dette i kommunikasjon med
          lærere/administratorer (for eksempel når man gir tilbakemeldinger
          eller stiller spørsmål). For lærere og administratorer gjelder dette i
          kommunikasjon med studenter, samt i læringsmateriellet som legges ut.
          Du er inneforstått med at det kan være et brudd på taushetsplikten din
          å legge ut pasientopplysninger eller kliniske data i Adapt.
        </p>
      </Section>
      <Section title="Dine rettigheter">
        <p>
          I følge GDPR har du flere rettigheter når det gjelder personrelaterte
          opplysninger om deg. De mest sentrale er:
        </p>
        <ul>
          <li>Rett til innsyn</li>
          <li>Rett til retting</li>
          <li>Rett til sletting</li>
          <li>Rett til dataportabilitet</li>
        </ul>
        <p>
          Les mer om alle dine rettigheter{" "}
          <a
            href="https://www.datatilsynet.no/rettigheter-og-plikter/den-registrertes-rettigheter"
            rel="noopener noreferrer"
            target="_blank"
          >
            her (åpnes i en ny fane).{" "}
          </a>
          For mer informasjon om hvordan Adapt fra Universitet i Oslo v/Jan
          Magnus Aronsen behandler dine opplysninger, og dine rettigheter, send
          mail til{" "}
          <a href="mailto:j.m.aronsen@medisin.uio.no?subject=Personvernerklæring">
            j.m.aronsen@medisin.uio.no
          </a>
        </p>
      </Section>
      <ConsentForm includeCompletedConsents={false} />
      <Section title="Godkjenning av brukervilkår">
        <p>
          Ved å ta i bruk denne løsningen <b>og</b> lagre dine valg for
          samtykkegrunnlagene under godtar du brukervilkårene. Du kan finne
          tilbake til dine samtykker på{" "}
          <Link to={urls.profile}>din profil</Link>.
        </p>
        <Button
          kind={haveSeenCurrentTermsVersion ? "disabled" : "primary"}
          onClick={handleHaveReadTerms}
        >
          {haveSeenCurrentTermsVersion ? "Allerede godkjent" : "Godkjenn"}
        </Button>
      </Section>
    </NormalContentLayout>
  );
};
